// Zustand store for the review screen. Owns:
//   - the proposed glossary (snapshotted at hydrate time)
//   - per-term action map (accepted | edited | rejected | pending | added)
//   - user-added terms
//   - a `dirty` flag that flips on any change and clears on a successful save
//   - a debounced save trigger so rapid clicks coalesce into one POST
//
// Defaults per plan/02_review_ui.md:
//   openapi -> accepted
//   user    -> accepted (added)
//   llm     -> pending

import { create } from "zustand";
import type { GlossaryEntry, TermKind } from "../data/types";
import type {
  ProposedGlossary,
  ReviewAction,
  ReviewStatePayload,
  TermReviewAction,
} from "./types";

export interface ReviewStore {
  docId: string;
  extractionGlossaryVersion: number;
  proposed: GlossaryEntry[];
  added: GlossaryEntry[];
  actions: Record<string, TermReviewAction>;
  dirty: boolean;
  saving: boolean;

  hydrate(glossary: ProposedGlossary, existing?: ReviewStatePayload | null): void;
  setAction(termId: string, action: ReviewAction): void;
  edit(termId: string, patch: {
    definition?: string;
    kind?: TermKind;
    alternates?: string[];
  }): void;
  addTerm(entry: Omit<GlossaryEntry, "origin" | "term_id"> & { term_id?: string }): GlossaryEntry;
  removeAdded(termId: string): void;
  bulkAccept(termIds: string[]): void;
  bulkReject(termIds: string[]): void;

  // counts derived; computed-via-selector by callers but the store carries
  // a helper so tests can read without subscribing.
  counts(): { total: number; pending: number; accepted: number; edited: number; rejected: number; added: number };

  // snapshot the state into the wire format. Pure; no side effects.
  snapshot(): ReviewStatePayload;

  // mark dirty/clean — used by the debounced save hook in api.ts
  markSaving(saving: boolean): void;
  markClean(): void;
}

const ACTOR = "demo";

function nowIso(): string {
  return new Date().toISOString();
}

function defaultActionFor(entry: GlossaryEntry): ReviewAction {
  if (entry.origin === "openapi") return "accepted";
  if (entry.origin === "user") return "added";
  return "pending";
}

export const useReviewStore = create<ReviewStore>((set, get) => ({
  docId: "",
  extractionGlossaryVersion: 0,
  proposed: [],
  added: [],
  actions: {},
  dirty: false,
  saving: false,

  hydrate(glossary, existing) {
    const actions: Record<string, TermReviewAction> = {};
    for (const t of glossary.terms) {
      const existingAction = existing?.actions?.[t.term_id];
      actions[t.term_id] = existingAction ?? {
        action: defaultActionFor(t),
        actor: ACTOR,
        at: nowIso(),
      };
    }
    const added = existing?.added ?? [];
    for (const a of added) {
      if (!actions[a.term_id]) {
        actions[a.term_id] = { action: "added", actor: ACTOR, at: nowIso() };
      }
    }
    set({
      docId: glossary.doc_id,
      extractionGlossaryVersion: glossary.extraction_glossary_version,
      proposed: glossary.terms,
      added,
      actions,
      dirty: false,
      saving: false,
    });
  },

  setAction(termId, action) {
    const prev = get().actions[termId];
    const next: TermReviewAction = {
      ...(prev ?? { actor: ACTOR, at: nowIso() }),
      action,
      at: nowIso(),
    };
    set((s) => ({ actions: { ...s.actions, [termId]: next }, dirty: true }));
  },

  edit(termId, patch) {
    const prev = get().actions[termId];
    const next: TermReviewAction = {
      actor: prev?.actor ?? ACTOR,
      at: nowIso(),
      action: "edited",
      edited_definition: patch.definition ?? prev?.edited_definition ?? null,
      edited_kind: patch.kind ?? prev?.edited_kind ?? null,
      edited_alternates: patch.alternates ?? prev?.edited_alternates ?? null,
    };
    set((s) => ({ actions: { ...s.actions, [termId]: next }, dirty: true }));
  },

  addTerm(entry) {
    const term_id = entry.term_id ?? slugify(entry.term);
    const created: GlossaryEntry = {
      term_id,
      term: entry.term,
      alternates: entry.alternates ?? [],
      definition: entry.definition,
      kind: entry.kind,
      origin: "user",
      source_block_id: entry.source_block_id ?? null,
      source_offset: entry.source_offset ?? null,
      source_length: entry.source_length ?? null,
    };
    set((s) => ({
      added: [...s.added, created],
      actions: {
        ...s.actions,
        [term_id]: { action: "added", actor: ACTOR, at: nowIso() },
      },
      dirty: true,
    }));
    return created;
  },

  removeAdded(termId) {
    set((s) => {
      const { [termId]: _drop, ...rest } = s.actions;
      return {
        added: s.added.filter((a) => a.term_id !== termId),
        actions: rest,
        dirty: true,
      };
    });
  },

  bulkAccept(termIds) {
    set((s) => {
      const next = { ...s.actions };
      for (const id of termIds) {
        const prev = next[id];
        next[id] = {
          ...(prev ?? { actor: ACTOR, at: nowIso() }),
          action: "accepted",
          at: nowIso(),
        };
      }
      return { actions: next, dirty: true };
    });
  },

  bulkReject(termIds) {
    set((s) => {
      const next = { ...s.actions };
      for (const id of termIds) {
        const prev = next[id];
        next[id] = {
          ...(prev ?? { actor: ACTOR, at: nowIso() }),
          action: "rejected",
          at: nowIso(),
        };
      }
      return { actions: next, dirty: true };
    });
  },

  counts() {
    const s = get();
    const c = { total: 0, pending: 0, accepted: 0, edited: 0, rejected: 0, added: 0 };
    for (const id of Object.keys(s.actions)) {
      c.total++;
      const a = s.actions[id]?.action;
      if (a === "pending") c.pending++;
      else if (a === "accepted") c.accepted++;
      else if (a === "edited") c.edited++;
      else if (a === "rejected") c.rejected++;
      else if (a === "added") c.added++;
    }
    return c;
  },

  snapshot() {
    const s = get();
    return {
      doc_id: s.docId,
      extraction_glossary_version: s.extractionGlossaryVersion,
      actions: s.actions,
      added: s.added,
      saved_at: nowIso(),
    };
  },

  markSaving(saving) {
    set({ saving });
  },

  markClean() {
    set({ dirty: false, saving: false });
  },
}));

function slugify(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}
